// src/components/Login/SignUp.jsx
// ✅ VERSÃO DEFINITIVA - NÍVEL GOOGLE/AIRBNB/SPOTIFY
// ✅ TEXTO VISÍVEL EM TODOS OS INPUTS
// ✅ USA O AUTH PROVIDER (NÃO FAZ NADA DIRETO)
// ✅ REDIRECIONAMENTO FEITO PELO PROVIDER
// ✅ SUPORTE COMPLETO A RECUPERAÇÃO DE SENHA
// 🔥 V2: BLINDADO — Network check + Timeout + Rate limit + Double submit blocker

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Lock, 
  Phone, 
  Building2, 
  Shield,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Sparkles,
  Briefcase,
  Home,
  Key,
  WifiOff
} from 'lucide-react';

// 👇 IMPORT DO CONTEXTO
import { AuthContext } from '../../contexts/AuthProvider';
import { useAuthBlindado } from '../../hooks/useAuthBlindado';

// Importação dos componentes UI
import PasswordStrength from './PasswordStrength';
import SocialButtons from './SocialButtons';
import TermsCheckbox from './TermsCheckbox';

// ============================================
// 🎨 CONSTANTES E CONFIGURAÇÕES
// ============================================

const STEPS = [
  { id: 1, name: 'Conta', icon: Mail },
  { id: 2, name: 'Perfil', icon: User },
  { id: 3, name: 'Empresa', icon: Building2 }
];

const ROLES = [
  { id: 'admin', label: 'Administrador', icon: Shield, description: 'Acesso total ao sistema' },
  { id: 'corretor', label: 'Corretor', icon: Briefcase, description: 'Gerenciar clientes e imóveis' },
  { id: 'usuario', label: 'Usuário', icon: Home, description: 'Visualização e contato' }
];

// ============================================
// 🎨 COMPONENTE PRINCIPAL
// ============================================

const SignUp = ({ onSuccess }) => {
  const navigate = useNavigate();
  
  // 👇 USAR O CONTEXTO + BLINDADO
  const { signUp } = useContext(AuthContext);
  const authBlindado = useAuthBlindado();
  
  // ============================================
  // 📊 ESTADOS
  // ============================================
  
  // Multi-step
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  
  // Form data
  const [formData, setFormData] = useState({
    // Passo 1: Conta
    email: '',
    password: '',
    confirmPassword: '',
    
    // Passo 2: Perfil
    name: '',
    phone: '',
    role: 'corretor',
    
    // Passo 3: Empresa
    company: '',
    cnpj: '',
    creci: '' // Opcional
  });
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  
  // ============================================
  // 🔍 VALIDAÇÕES EM TEMPO REAL
  // ============================================
  
  const validateStep = useCallback((step, data = formData) => {
    const stepErrors = {};
    
    if (step === 1) {
      // Email validation
      if (!data.email) {
        stepErrors.email = 'E-mail é obrigatório';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        stepErrors.email = 'E-mail inválido';
      }
      
      // Password validation
      if (!data.password) {
        stepErrors.password = 'Senha é obrigatória';
      } else if (data.password.length < 8) {
        stepErrors.password = 'Mínimo 8 caracteres';
      }
      
      // Confirm password
      if (data.password !== data.confirmPassword) {
        stepErrors.confirmPassword = 'As senhas não coincidem';
      }
    }
    
    if (step === 2) {
      if (!data.name) {
        stepErrors.name = 'Nome é obrigatório';
      } else if (data.name.length < 3) {
        stepErrors.name = 'Nome muito curto';
      }
      
      if (!data.phone) {
        stepErrors.phone = 'Telefone é obrigatório';
      } else if (!/^\(?[1-9]{2}\)? ?9?[0-9]{4}[ -]?[0-9]{4}$/.test(data.phone.replace(/\D/g, ''))) {
        stepErrors.phone = 'Telefone inválido';
      }
    }
    
    if (step === 3) {
      if (!data.company) {
        stepErrors.company = 'Nome da empresa é obrigatório';
      }
      
      if (data.cnpj && data.cnpj.replace(/\D/g, '').length !== 14) {
        stepErrors.cnpj = 'CNPJ inválido (deve ter 14 dígitos)';
      }
    }
    
    return stepErrors;
  }, [formData]);
  
  const isStepValid = useCallback((step) => {
    const stepErrors = validateStep(step);
    return Object.keys(stepErrors).length === 0;
  }, [validateStep]);
  
  // Validação completa para submissão
  const validateAll = useCallback(() => {
    const allErrors = {
      ...validateStep(1),
      ...validateStep(2),
      ...validateStep(3)
    };
    
    if (!acceptedTerms) {
      allErrors.terms = 'Você deve aceitar os termos de uso';
    }
    
    return allErrors;
  }, [validateStep, acceptedTerms]);
  
  // ============================================
  // 🎯 HANDLERS DE EVENTOS
  // ============================================
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Marcar como touched para validação
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validar campo específico
    const fieldErrors = validateStep(currentStep);
    if (fieldErrors[name]) {
      setErrors(prev => ({ ...prev, [name]: fieldErrors[name] }));
    }
  };
  
  const handleNextStep = () => {
    const stepErrors = validateStep(currentStep);
    
    if (Object.keys(stepErrors).length === 0) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setErrors(stepErrors);
      // Marcar todos os campos como touched
      Object.keys(stepErrors).forEach(field => {
        setTouched(prev => ({ ...prev, [field]: true }));
      });
    }
  };
  
  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // ============================================
  // 🚀 V2: HANDLE SUBMIT BLINDADO
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 🔥 V2: Verificar conexão
    if (!navigator.onLine) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Sem conexão com a internet. Verifique sua rede.' 
      });
      return;
    }
    
    // ✅ Validação completa
    const allErrors = validateAll();
    
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      setSubmitStatus({ type: 'error', message: 'Corrija os erros antes de continuar' });
      
      // Levar para o passo com erro
      if (allErrors.email || allErrors.password || allErrors.confirmPassword) {
        setCurrentStep(1);
      } else if (allErrors.name || allErrors.phone) {
        setCurrentStep(2);
      } else if (allErrors.company || allErrors.cnpj) {
        setCurrentStep(3);
      }
      
      return;
    }
    
    setLoading(true);
    setSubmitStatus(null);
    
    try {
      // 🔥 V2: Usar authBlindado para timeout + rate limit + retry
      const result = await authBlindado.signUp(formData);
      
      if (result.success) {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Cadastro realizado! Redirecionando...' 
        });
        
        // Disparar evento de sucesso
        window.dispatchEvent(new CustomEvent('auth:signup:success', {
          detail: { email: formData.email }
        }));
        
        // Callback de sucesso
        if (onSuccess) {
          onSuccess({ email: formData.email });
        }
        
        // Redirecionar
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
        
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: result.error || 'Erro ao realizar cadastro. Tente novamente.' 
        });
      }
      
    } catch (error) {
      console.error('❌ Erro no cadastro:', error);
      
      // 🔥 V2: Mapear erros do blindado
      if (error?.code === 'custom/offline') {
        setSubmitStatus({ type: 'error', message: 'Sem conexão com a internet.' });
      } else if (error?.code === 'custom/rate-limited') {
        setSubmitStatus({ type: 'error', message: 'Muitas tentativas. Aguarde 30 segundos.' });
      } else if (error?.code === 'custom/timeout') {
        setSubmitStatus({ type: 'error', message: 'Tempo limite excedido. Tente novamente.' });
      } else if (error?.code === 'custom/double-submit') {
        setSubmitStatus({ type: 'error', message: 'Cadastro já em andamento. Aguarde.' });
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: 'Erro ao realizar cadastro. Tente novamente.' 
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  // ============================================
  // 🎨 RENDERIZAÇÃO DOS PASSOS
  // ============================================
  
  const renderStep1 = () => (
    <motion.div
      key="step1"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-5"
    >
      {/* 🔥 V2: Indicador offline */}
      {!navigator.onLine && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
          <WifiOff className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-xs text-amber-700">Sem conexão com a internet</span>
        </div>
      )}

      {/* Email */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4" />
            E-mail profissional
          </div>
        </label>
        <div className="relative">
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`
              w-full px-4 py-3 pl-11 border rounded-xl
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500/20
              text-slate-900
              placeholder:text-slate-400
              ${touched.email && errors.email 
                ? 'border-red-300 bg-red-50' 
                : touched.email && !errors.email
                  ? 'border-green-300 bg-green-50'
                  : 'border-slate-300'
              }
            `}
            placeholder="seu@email.com"
            disabled={loading}
          />
          <Mail className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
          
          {touched.email && !errors.email && formData.email && (
            <CheckCircle className="absolute right-3 top-3.5 w-5 h-5 text-green-500" />
          )}
        </div>
        <AnimatePresence>
          {touched.email && errors.email && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-xs text-red-500 flex items-center gap-1"
            >
              <AlertCircle className="w-3 h-3" />
              {errors.email}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Senha */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4" />
            Senha
          </div>
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`
              w-full px-4 py-3 pl-11 pr-11 border rounded-xl
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500/20
              text-slate-900
              placeholder:text-slate-400
              ${touched.password && errors.password 
                ? 'border-red-300 bg-red-50' 
                : touched.password && !errors.password && formData.password.length >= 8
                  ? 'border-green-300 bg-green-50'
                  : 'border-slate-300'
              }
            `}
            placeholder="••••••••"
            disabled={loading}
          />
          <Lock className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3.5"
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5 text-slate-400 hover:text-slate-600" />
            ) : (
              <Eye className="w-5 h-5 text-slate-400 hover:text-slate-600" />
            )}
          </button>
        </div>
        
        {/* Medidor de força */}
        <PasswordStrength password={formData.password} />
      </div>

      {/* Confirmar Senha */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <Key className="w-4 h-4" />
            Confirmar senha
          </div>
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`
              w-full px-4 py-3 pl-11 pr-11 border rounded-xl
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500/20
              text-slate-900
              placeholder:text-slate-400
              ${touched.confirmPassword && errors.confirmPassword 
                ? 'border-red-300 bg-red-50' 
                : touched.confirmPassword && !errors.confirmPassword && formData.confirmPassword
                  ? 'border-green-300 bg-green-50'
                  : 'border-slate-300'
              }
            `}
            placeholder="••••••••"
            disabled={loading}
          />
          <Key className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-3.5"
          >
            {showConfirmPassword ? (
              <EyeOff className="w-5 h-5 text-slate-400 hover:text-slate-600" />
            ) : (
              <Eye className="w-5 h-5 text-slate-400 hover:text-slate-600" />
            )}
          </button>
        </div>
        <AnimatePresence>
          {touched.confirmPassword && errors.confirmPassword && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-xs text-red-500 flex items-center gap-1"
            >
              <AlertCircle className="w-3 h-3" />
              {errors.confirmPassword}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-5"
    >
      {/* Nome completo */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4" />
            Nome completo
          </div>
        </label>
        <div className="relative">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`
              w-full px-4 py-3 pl-11 border rounded-xl
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500/20
              text-slate-900
              placeholder:text-slate-400
              ${touched.name && errors.name 
                ? 'border-red-300 bg-red-50' 
                : touched.name && !errors.name && formData.name
                  ? 'border-green-300 bg-green-50'
                  : 'border-slate-300'
              }
            `}
            placeholder="Seu nome completo"
            disabled={loading}
          />
          <User className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
          {touched.name && !errors.name && formData.name && (
            <CheckCircle className="absolute right-3 top-3.5 w-5 h-5 text-green-500" />
          )}
        </div>
        <AnimatePresence>
          {touched.name && errors.name && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-xs text-red-500 flex items-center gap-1"
            >
              <AlertCircle className="w-3 h-3" />
              {errors.name}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Telefone */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <Phone className="w-4 h-4" />
            Telefone (WhatsApp)
          </div>
        </label>
        <div className="relative">
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`
              w-full px-4 py-3 pl-11 border rounded-xl
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500/20
              text-slate-900
              placeholder:text-slate-400
              ${touched.phone && errors.phone 
                ? 'border-red-300 bg-red-50' 
                : touched.phone && !errors.phone && formData.phone
                  ? 'border-green-300 bg-green-50'
                  : 'border-slate-300'
              }
            `}
            placeholder="(11) 99999-9999"
            disabled={loading}
          />
          <Phone className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
          {touched.phone && !errors.phone && formData.phone && (
            <CheckCircle className="absolute right-3 top-3.5 w-5 h-5 text-green-500" />
          )}
        </div>
        <AnimatePresence>
          {touched.phone && errors.phone && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-xs text-red-500 flex items-center gap-1"
            >
              <AlertCircle className="w-3 h-3" />
              {errors.phone}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Tipo de perfil */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="w-4 h-4" />
            Tipo de perfil
          </div>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {ROLES.map((role) => {
            const Icon = role.icon;
            const isSelected = formData.role === role.id;
            
            return (
              <motion.button
                key={role.id}
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setFormData(prev => ({ ...prev, role: role.id }))}
                className={`
                  p-4 rounded-xl border-2 text-left
                  transition-all duration-200
                  ${isSelected 
                    ? 'border-blue-500 bg-blue-50 shadow-md' 
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                  }
                `}
                disabled={loading}
              >
                <div className={`p-2 rounded-lg w-fit mb-2 ${
                  isSelected ? 'bg-blue-500' : 'bg-slate-100'
                }`}>
                  <Icon className={`w-4 h-4 ${
                    isSelected ? 'text-white' : 'text-slate-600'
                  }`} />
                </div>
                <div className="font-medium text-slate-900">{role.label}</div>
                <div className="text-xs text-slate-500 mt-1">{role.description}</div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      key="step3"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-5"
    >
      {/* Nome da empresa */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4" />
            Nome da empresa
          </div>
        </label>
        <div className="relative">
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`
              w-full px-4 py-3 pl-11 border rounded-xl
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500/20
              text-slate-900
              placeholder:text-slate-400
              ${touched.company && errors.company 
                ? 'border-red-300 bg-red-50' 
                : touched.company && !errors.company && formData.company
                  ? 'border-green-300 bg-green-50'
                  : 'border-slate-300'
              }
            `}
            placeholder="Imóveis Pro LTDA"
            disabled={loading}
          />
          <Building2 className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
          {touched.company && !errors.company && formData.company && (
            <CheckCircle className="absolute right-3 top-3.5 w-5 h-5 text-green-500" />
          )}
        </div>
        <AnimatePresence>
          {touched.company && errors.company && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-xs text-red-500 flex items-center gap-1"
            >
              <AlertCircle className="w-3 h-3" />
              {errors.company}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* CNPJ (opcional) */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="w-4 h-4" />
            CNPJ <span className="text-xs text-slate-400 font-normal">(opcional)</span>
          </div>
        </label>
        <div className="relative">
          <input
            type="text"
            name="cnpj"
            value={formData.cnpj}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`
              w-full px-4 py-3 pl-11 border rounded-xl
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500/20
              text-slate-900
              placeholder:text-slate-400
              ${touched.cnpj && errors.cnpj 
                ? 'border-red-300 bg-red-50' 
                : 'border-slate-300'
              }
            `}
            placeholder="00.000.000/0000-00"
            disabled={loading}
          />
          <Briefcase className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
        </div>
        <AnimatePresence>
          {touched.cnpj && errors.cnpj && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-xs text-red-500 flex items-center gap-1"
            >
              <AlertCircle className="w-3 h-3" />
              {errors.cnpj}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* CRE/CRECI (opcional) */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4" />
            CRE/CRECI <span className="text-xs text-slate-400 font-normal">(opcional)</span>
          </div>
        </label>
        <div className="relative">
          <input
            type="text"
            name="creci"
            value={formData.creci}
            onChange={handleInputChange}
            className={`
              w-full px-4 py-3 pl-11 border border-slate-300 rounded-xl
              text-slate-900
              placeholder:text-slate-400
            `}
            placeholder="CRECI/SP 123456"
            disabled={loading}
          />
          <Shield className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
        </div>
      </div>
    </motion.div>
  );

  // ============================================
  // 🎨 RENDER PRINCIPAL
  // ============================================
  
  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header com steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = completedSteps.has(step.id);
            
            return (
              <div key={step.id} className="flex-1 last:flex-none">
                <div className="flex items-center">
                  <div className="relative">
                    <motion.div
                      animate={{
                        scale: isActive ? 1.1 : 1,
                        backgroundColor: isCompleted ? '#10b981' : isActive ? '#3b82f6' : '#e2e8f0'
                      }}
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center
                        transition-colors duration-200
                      `}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : (
                        <Icon className={`w-5 h-5 ${
                          isActive ? 'text-white' : 'text-slate-500'
                        }`} />
                      )}
                    </motion.div>
                    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-medium text-slate-500 whitespace-nowrap">
                      {step.name}
                    </span>
                  </div>
                  {step.id < 3 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      completedSteps.has(step.id) ? 'bg-green-500' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <motion.h2 
          key={currentStep}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-slate-900 mb-2"
        >
          {currentStep === 1 && 'Crie sua conta'}
          {currentStep === 2 && 'Complete seu perfil'}
          {currentStep === 3 && 'Dados da empresa'}
        </motion.h2>
        <p className="text-slate-600">
          {currentStep === 1 && 'Informações básicas para acesso à plataforma'}
          {currentStep === 2 && 'Como você quer ser visto pelos clientes'}
          {currentStep === 3 && 'Opcional, mas recomendado para imobiliárias'}
        </p>
      </div>

      {/* Formulário */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <AnimatePresence mode="wait">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </AnimatePresence>

        {/* Termos de uso */}
        {currentStep === 3 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="pt-4"
          >
            <TermsCheckbox
              checked={acceptedTerms}
              onChange={setAcceptedTerms}
              error={errors.terms}
              showModal={true}
              size="md"
            />
          </motion.div>
        )}

        {/* Botões de navegação */}
        <div className="flex gap-3 pt-4">
          {currentStep > 1 && (
            <motion.button
              type="button"
              onClick={handlePrevStep}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 px-6 py-3 border-2 border-slate-200 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-all"
              disabled={loading}
            >
              Voltar
            </motion.button>
          )}
          
          {currentStep < 3 ? (
            <motion.button
              type="button"
              onClick={handleNextStep}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold text-white hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
              disabled={loading}
            >
              Continuar
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          ) : (
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                flex-1 px-6 py-3 rounded-xl font-semibold text-white
                transition-all flex items-center justify-center gap-2
                ${loading || !navigator.onLine
                  ? 'bg-slate-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 shadow-lg hover:shadow-xl'
                }
              `}
              disabled={loading || !navigator.onLine}
            >
              {loading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  Cadastrando...
                </>
              ) : !navigator.onLine ? (
                <>
                  <WifiOff className="w-4 h-4" />
                  Sem conexão
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Finalizar cadastro
                </>
              )}
            </motion.button>
          )}
        </div>

        {/* Status message */}
        <AnimatePresence>
          {submitStatus && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`
                p-4 rounded-xl flex items-center gap-3
                ${submitStatus.type === 'success' 
                  ? 'bg-emerald-50 border border-emerald-200' 
                  : 'bg-red-50 border border-red-200'
                }
              `}
            >
              {submitStatus.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              <p className={submitStatus.type === 'success' ? 'text-emerald-700' : 'text-red-700'}>
                {submitStatus.message}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Divisor e login social */}
        {currentStep === 1 && (
          <>
            <SocialButtons 
              onSuccess={(data) => console.log('Social signup:', data)}
              disabled={loading || !navigator.onLine}
              mode="mock"
            />
            
            <p className="text-center text-sm text-slate-600">
              Já tem uma conta?{' '}
              <Link 
                to="/login" 
                className="font-medium text-blue-600 hover:text-blue-700"
              >
                Fazer login
              </Link>
            </p>
          </>
        )}
      </form>
    </div>
  );
};

// ✅ Otimização: memo para evitar re-renders desnecessários
export default React.memo(SignUp);